import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { CommunicationTemplateType, AITone, AILength } from "../../../Api/emailTemplatesApi";
import { B13, B13Ink, B13TypeScale, tint12 } from "../../../theme/b13Tokens";

type GenerateFn = (input: {
  category?: string | null;
  category_label?: string | null;
  name?: string;
  subject?: string;
  tone?: AITone;
  length?: AILength;
  purpose?: string;
}) => Promise<{ body: string; variables: string[] }>;

interface Props {
  channel: CommunicationTemplateType;
  category: string | null;
  categoryLabel: string | null;
  name: string;
  subject?: string;
  onClose: () => void;
  onInsert: (body: string, replace: boolean) => void;
  generate: GenerateFn;
}

const TONES: AITone[]     = ["Professional", "Friendly", "Warm", "Formal", "Corporate", "Sales-focused", "Luxury"];
const LENGTHS: AILength[] = ["Short", "Medium", "Detailed"];

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 44, borderRadius: "10px", bgcolor: "#FFFFFF",
    "& fieldset": { borderColor: B13.line },
    "&:hover fieldset": { borderColor: tint12(B13.primary) },
    "&.Mui-focused fieldset": { borderColor: B13.primary, borderWidth: "1px" },
  },
  "& .MuiOutlinedInput-input": { fontSize: 13.5, color: B13.ink },
} as const;

const AIAssistantPanel: React.FC<Props> = ({
  channel, category, categoryLabel, name, subject, onClose, onInsert, generate,
}) => {
  const [purpose, setPurpose] = useState("");
  const [tone, setTone]       = useState<AITone>("Professional");
  const [length, setLength]   = useState<AILength>(channel === "sms" ? "Short" : "Medium");
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [draft, setDraft]     = useState<string | null>(null);
  const [usedVars, setUsedVars] = useState<string[]>([]);

  const canGenerate = purpose.trim().length > 3 && !busy;

  const run = async () => {
    if (!canGenerate) return;
    setBusy(true);
    setError(null);
    try {
      // The AI endpoint is server-side (Anthropic Claude). We pass the
      // current editor state as light context. The server resolves the
      // variable list against the purpose so unknown tokens don't leak
      // into the draft.
      const res = await generate({
        category, category_label: categoryLabel,
        name: name || undefined,
        subject: subject || undefined,
        tone, length, purpose: purpose.trim(),
      });
      setDraft(res.body);
      setUsedVars(res.variables || []);
    } catch (e: any) {
      setError(e?.message || "AI generation failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${B13.line}`, display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 2,
          bgcolor: "#7c3aed14", color: "#7c3aed",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <AutoAwesomeOutlinedIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ ...B13TypeScale.h3 }}>Write with AI</Typography>
          <Typography sx={{ ...B13TypeScale.caption, color: B13Ink.ink2 }}>
            Drafts a {channel === "sms" ? "concise SMS" : "professional email"} body — you stay in control before it lands in the editor.
          </Typography>
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>

      <DialogContent sx={{ bgcolor: "#F7F9FC", p: 3 }}>
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="What would you like to write?"
            placeholder={
              channel === "sms"
                ? "e.g. Payment reminder to parents whose invoice is overdue"
                : "e.g. Welcome email for a new student joining Math grade 6"
            }
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            multiline
            minRows={3}
            sx={fieldSx}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              select fullWidth label="Tone" value={tone}
              onChange={(e) => setTone(e.target.value as AITone)} sx={fieldSx}
            >
              {TONES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField
              select fullWidth label="Length" value={length}
              onChange={(e) => setLength(e.target.value as AILength)} sx={fieldSx}
            >
              {LENGTHS.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
            </TextField>
          </Stack>

          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          {draft && (
            <Box sx={{ bgcolor: "#FFFFFF", borderRadius: 2, border: `1px solid ${B13.line}`, p: 2 }}>
              <Typography sx={{ ...B13TypeScale.caption, fontWeight: 700, color: B13Ink.ink3, textTransform: "uppercase", letterSpacing: 0.6, mb: 1 }}>
                AI draft
              </Typography>
              <Typography
                component="pre"
                sx={{
                  fontFamily: "inherit", whiteSpace: "pre-wrap", fontSize: 13.5,
                  lineHeight: 1.55, color: B13.ink, m: 0,
                }}
              >
                {draft}
              </Typography>
              {usedVars.length > 0 && (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" gap={0.75} sx={{ mt: 1.5 }}>
                  {usedVars.map((v) => (
                    <Chip key={v} size="small" label={`{{${v}}}`}
                      sx={{ fontSize: 11, height: 22, fontFamily: "monospace", bgcolor: tint12(B13.primary), color: B13.primary }} />
                  ))}
                </Stack>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>

      <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${B13.line}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Stack direction="row" spacing={1}>
          {draft && (
            <>
              <Button
                onClick={run}
                disabled={!canGenerate}
                sx={{ textTransform: "none" }}
                startIcon={busy ? <CircularProgress size={16} /> : <AutoAwesomeOutlinedIcon />}
              >
                Regenerate
              </Button>
              <Button
                onClick={() => onInsert(draft, false)}
                variant="outlined"
                sx={{ textTransform: "none", borderRadius: "10px" }}
              >
                Append to body
              </Button>
              <Button
                onClick={() => onInsert(draft, true)}
                variant="contained"
                sx={{
                  textTransform: "none", bgcolor: "#7c3aed",
                  "&:hover": { bgcolor: "#6d28d9" }, borderRadius: "10px",
                  fontWeight: 600,
                }}
              >
                Insert into template
              </Button>
            </>
          )}
          {!draft && (
            <Button
              onClick={run}
              disabled={!canGenerate}
              variant="contained"
              startIcon={busy ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <AutoAwesomeOutlinedIcon />}
              sx={{
                textTransform: "none", bgcolor: "#7c3aed",
                "&:hover": { bgcolor: "#6d28d9" }, borderRadius: "10px",
                fontWeight: 600, px: 2.5,
              }}
            >
              {busy ? "Generating…" : "Generate"}
            </Button>
          )}
        </Stack>
      </Box>
    </Dialog>
  );
};

export default AIAssistantPanel;
